import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import { memo } from 'react';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  gray01,
  gray02,
  gray04,
  gray06,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import styled from '@emotion/styled';
import MonthEventCell from '../MonthEventCell';
import { useMonthGrid } from './hooks';
import { calendarNewEventPrefix } from '../DayTimeGrid/Event';
import { DateTime } from 'luxon';
import { Behavior, MergeableEventListItem, Schedule } from '../reducer';
import { formatToTimeShort } from 'infras/datetime';
import { css } from '@emotion/react';
import Icon from 'presentations/views/components/atoms/Icon';

export interface Props {
  startedAt: DateTime;
  page: number;
  onClickEvent?: (event: MergeableEventListItem, el: HTMLElement) => void;
  onClickMoreEvent?: (date: DateTime, el: HTMLElement) => void;
  onClickDay?: (date: DateTime, el: HTMLElement) => void;
  selectedEventId?: string;
  onUpdateSchedule?: (schedule: Schedule) => void;
  className?: string;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const { state, actions } = useMonthGrid({
    startedAt: props.startedAt,
    page: props.page,
    onClickEvent: props.onClickEvent,
    onClickMoreEvent: props.onClickMoreEvent,
    onClickDay: props.onClickDay,
    onUpdateSchedule: props.onUpdateSchedule,
  });
  return (
    <Content
      className={props.className}
      maxCols={state.dates.length}
      maxRows={state.maxRow}
    >
      {!state.dragDirection &&
        state.draggingGhostEvent &&
        state.draggingGhostEvent.isAllDay && (
          <GhostEventCell
            eventId={state.draggingGhostEvent.uid}
            x={state.draggingGhostEvent.x}
            xSpan={state.draggingGhostEvent.xSpan}
            y={state.draggingGhostEvent.y}
            ySpan={state.draggingGhostEvent.ySpan}
            color={state.draggingGhostEvent.color}
            name={state.draggingGhostEvent.name}
            isAllDay={state.draggingGhostEvent.isAllDay}
            icon={
              state.draggingGhostEvent.behavior === Behavior.Work && (
                <ServiceIcon name="check-circle" />
              )
            }
          />
        )}
      {state.draggingEvent && state.draggingEvent.isAllDay && (
        <DragEventCell
          eventId={state.draggingEvent.uid}
          x={state.draggingEvent.x}
          xSpan={state.draggingEvent.xSpan}
          y={state.draggingEvent.y}
          ySpan={state.draggingEvent.ySpan}
          color={state.draggingEvent.color}
          name={state.draggingEvent.name}
          isAllDay={state.draggingEvent.isAllDay}
          icon={
            state.draggingEvent.behavior === Behavior.Work && (
              <ServiceIcon name="check-circle" />
            )
          }
        />
      )}
      {!state.isLoading &&
        state.events.map((event) => {
          if (
            event.yPosition === undefined ||
            event.yPosition >= state.maxEventCountInCell
          ) {
            return null;
          }
          return (
            <StyledEventCell
              key={event.layoutId}
              className={
                event.uid === 'new-event'
                  ? `${calendarNewEventPrefix}`
                  : 'js-month-events'
              }
              isDraggable
              eventId={event.uid}
              isDragging={event.uid === state.draggingEvent?.uid}
              x={event.x}
              y={event.y}
              xSpan={event.xSpan}
              xOriginalSpan={event.xOriginalSpan}
              ySpan={event.ySpan}
              yPosition={event.yPosition}
              color={event.color}
              name={event.name}
              onDragStart={actions.onEventDragStart}
              onDragEnd={actions.onEventDragEnd}
              onClick={actions.onClickEvent}
              onDragHandlerMove={actions.onEventDragHandlerMove}
              isSelected={
                props.selectedEventId === event.uid || event.uid === 'new-event'
              }
              responseStatus={event.responseStatus}
              isAllDay={event.isAllDay}
              timeText={formatToTimeShort(event.startedAt)}
              isDisabled={!!props.onClickDay}
              icon={
                event.behavior === Behavior.Work && (
                  <ServiceIcon name="check-circle" />
                )
              }
            />
          );
        })}
      {state.moreItems.map((moreItem, i) => {
        return (
          <MoreEventItem
            key={i}
            x={moreItem.x}
            y={moreItem.y}
            data-date={moreItem.date}
            yPosition={state.maxEventCountInCell}
            onClick={actions.onClickMoreEvent}
          >
            {t('Event More', {
              count: moreItem.maxPosition - state.maxEventCountInCell,
            })}
          </MoreEventItem>
        );
      })}

      {/* {state.creatingEvent && (
        <NewEventCell
          isDraggable
          className={`${calendarNewEventPrefix}${state.creatingEvent.x!}-${state
            .creatingEvent.y!}`}
          eventId={'new-month-event'}
          data-event-id={'new-month-event'}
          isDragging={'new-month-event' === state.draggingEvent?.eventId}
          x={state.creatingEvent.x!}
          y={state.creatingEvent.y!}
          ySpan={state.creatingEvent.ySpan!}
          yPosition={0}
          color={state.creatingEvent.color}
          name={state.creatingEvent.name}
          onDragStart={actions.onEventDragStart}
          onDragEnd={actions.onEventDragEnd}
          isAllDay={state.creatingEvent.isAllDay}
        />
      )} */}

      {state.frames.map(
        (frame: { date: DateTime; isHide: boolean }, i: number) => (
          <DateCell
            key={frame.date.toISO()}
            isHoliday={[0, 6].includes(frame.date.weekday % 7)}
            iconUrl={state.draggingEvent ? null : state.addIconUrl}
          >
            {!frame.isHide && (
              <DateContent
                draggable
                data-frame-id={i}
                data-slot-id={i}
                onDrop={actions.onEventDrop}
                onDragEnter={actions.onEventDragEnter}
                onDragOver={actions.onEventDragOver}
                onClick={
                  props.onClickDay ? actions.onClickDay : actions.onClickAddSlot
                }
                // onDragStart={actions.onDayDragStart}
                // onDragEnter={
                //   state.draggingEvent
                //     ? actions.onEventDragEnter
                //     : actions.onDayDragEnter
                // }
                // onDragOver={actions.onEventDragOver}
                // onDragEnd={actions.onDayDragEnd}
                // onClick={actions.onClickAddSlot}
              >
                <DateTitle>
                  <DateDayTitle
                    isToday={
                      frame.date.toFormat('yyyyMMdd') ===
                      state.now.toFormat('yyyyMMdd')
                    }
                  >
                    {frame.date.day}
                  </DateDayTitle>
                  {frame.date.day === 1 && i < 7 && (
                    <DateMonthTitle>
                      {frame.date.toFormat(t('Calendar Month'))}
                    </DateMonthTitle>
                  )}
                </DateTitle>
              </DateContent>
            )}
          </DateCell>
        ),
      )}
    </Content>
  );
});
const Content = styled.div<{
  maxRows: number;
  maxCols: number;
}>`
  width: inherit;
  flex: 1 1 0%;
  display: grid;
  grid-template-columns: ${(props) =>
    `repeat(${props.maxCols}, minmax(18px, 1fr))`};
  grid-template-rows: ${(props) =>
    `repeat(${props.maxRows + 1}, minmax(104px, 1fr))`};
  grid-auto-flow: row;
  position: relative;
`;
const DateCell = styled.div<{
  iconUrl?: string | null;
  isDisabled?: boolean;
  isHoliday?: boolean;
}>`
  border-left: 1px solid ${gray04};
  border-bottom: 1px solid ${gray04};
  background-color: ${(props) => props.isHoliday && gray06};
  color: ${(props) => (props.isHoliday ? gray02 : gray01)};
  position: relative;
  cursor: ${(props) => props.isDisabled && 'not-allowed'};
`;
const DateContent = styled.div`
  height: 166px;
  ${mediaMax.smartphone} {
    height: 104px;
  }
`;
const DateTitle = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  padding: ${spacing(6)} ${spacing(8)};
  ${mediaMax.smartphone} {
    font-size: ${fontSize(14)};
    padding: ${spacing(4)} ${spacing(4)};
  }
`;
const DateDayTitle = styled.div<{ isToday: boolean }>`
  ${(props) =>
    props.isToday &&
    css`
      color: ${whiteFix(props)};
      background-color: ${primary(props)};
      border-radius: 50%;
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
    `};
`;
const DateMonthTitle = styled.div`
  margin-right: ${spacing(4)};
  background-color: ${gray02};
  color: ${whiteFix};
  padding: 0 ${spacing(8)};
  border-radius: ${borderRadius(16)};

  ${mediaMax.smartphone} {
    color: ${gray01};
    padding: 0;
    background-color: transparent;
    /* display: none; */
  }
`;
const GhostEventCell = styled(MonthEventCell)`
  opacity: 0.5;
  user-select: none;
  pointer-events: none;
`;
const DragEventCell = styled(MonthEventCell)`
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
  z-index: 3;
  user-select: none;
  pointer-events: none;
`;
const StyledEventCell = styled(MonthEventCell)<{ isDisabled: boolean }>`
  z-index: 2;
  ${(props) =>
    props.isDisabled &&
    css`
      user-select: none;
      pointer-events: none;
    `};
`;
const NewEventCell = styled(MonthEventCell)`
  z-index: 3;
`;
const MoreEventItem = styled.div<{
  x: number;
  y: number;
  yPosition?: number;
}>`
  grid-area: ${(props) =>
    `${props.y + 1} / ${
      props.x + 1
    } / span 1 / span 1`}; // 縦位置 / 横位置 / 縦枠幅 / 横枠幅
  cursor: pointer;
  top: ${(props) => `calc(36px + ${props.yPosition} * 20px)`};
  left: 4px;
  height: 16px;
  position: absolute;
  font-size: ${fontSize(11)};
  color: ${gray01};
  z-index: 2;
  ${mediaMax.smartphone} {
    top: ${(props) => `calc(28px + ${props.yPosition} * 18px)`};
    height: 14px;
  }
`;
const ServiceIcon = styled(Icon)`
  width: 12px !important;
  height: 12px !important;
  color: inherit;
  margin-right: ${spacing(2)};
  :last-of-type {
    margin-right: 0;
  }
`;

export default Component;
